import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@pantry/ui';
import { useTransferOwnership, type MemberWithUser } from '@pantry/api-client';
import {
  ArrowRightStartOnRectangleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface TransferOwnershipDialogProps {
  householdId: string;
  member: MemberWithUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TransferOwnershipDialog({
  householdId,
  member,
  open,
  onOpenChange,
}: TransferOwnershipDialogProps) {
  const { t } = useLingui();
  const [error, setError] = useState<string | null>(null);

  const transferOwnership = useTransferOwnership(householdId);

  const handleTransfer = async () => {
    setError(null);

    try {
      await transferOwnership.mutateAsync(member.userId);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to transfer ownership`;
      setError(message);
    }
  };

  const memberName = member.user.name || member.user.email;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10">
              <ArrowRightStartOnRectangleIcon className="h-5 w-5 text-amber-600" />
            </div>
            <Trans>Transfer Ownership</Trans>
          </AlertDialogTitle>
          <AlertDialogDescription className="pt-2 space-y-3">
            <p>
              <Trans>
                Are you sure you want to transfer ownership to{' '}
                <span className="font-medium text-foreground">{memberName}</span>?
              </Trans>
            </p>
            <div className="rounded-xl bg-destructive/5 border border-destructive/20 p-3 flex gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">
                <Trans>
                  This action cannot be undone. You will become an admin and lose owner privileges
                  including the ability to delete the household.
                </Trans>
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
            {error}
          </div>
        )}
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel disabled={transferOwnership.isPending} className="rounded-xl">
            <Trans>Cancel</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleTransfer}
            disabled={transferOwnership.isPending}
            className="rounded-xl"
          >
            {transferOwnership.isPending ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                <Trans>Transferring...</Trans>
              </div>
            ) : (
              <Trans>Transfer Ownership</Trans>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
